import { usePreferredCaregiver } from '@/features/booking/shared/model/usePreferredCaregiver';
import { Card, NavigationItem } from '@/components';
import { Utils } from '@/shared/Utils';
import { CaregiverAvatar } from '@/features/booking/shared/ui/CaregiverAvatar';
import { format, R } from '@/features/booking/resources/Texts.resx';
import { useEffect, useState } from 'react';
import { bookingDraftAtom } from '@/features/booking/shared/model/bookingState';
import { useAtomValue } from 'jotai';
import { cn } from '@/lib/cn';
import { CaregiverSelectionStatus, ProductDto } from '@/shared/interfaces';

type SelectCaregiverProps = {
    product: ProductDto;
    onSubmitCaregiver: () => void;
    asCard?: boolean;
};

export const SelectCaregiver = ({
    product,
    onSubmitCaregiver,
    asCard = true,
}: SelectCaregiverProps) => {
    const draftState = useAtomValue(bookingDraftAtom);

    const { caregivers, preferredCaregiverUid, chooseCaregiver } =
        usePreferredCaregiver({
            consultationType: product.ConsultationType,
            performInitialFetch:
                product.CaregiverSelectionStatus !==
                CaregiverSelectionStatus.Disabled,
        });

    const [shouldSubmit, setShouldSubmit] = useState(false);

    // this is to ensure draftState is updated when we submit
    useEffect(() => {
        if (!shouldSubmit || !draftState) return;
        onSubmitCaregiver();
        setShouldSubmit(false);
    }, [shouldSubmit, draftState?.preferredCaregiverUid]);

    const handleClick = (uid: string) => {
        chooseCaregiver(uid, true);
        setShouldSubmit(true);
    };

    const content = (
        <div className="flex flex-col gap-4">
            <div>
                <h2 className="mb-6">{R.SelectCaregiverTitle}</h2>
                <div>{R.SelectCaregiverDescription}</div>
            </div>

            {caregivers.map((caregiver) => {
                const isSelected = preferredCaregiverUid === caregiver.Uid;
                const description = caregiver.Description ?? '';
                const nextAvailableTime = caregiver.NextAvailableTime
                    ? Utils.formatRelativeDateTime(caregiver.NextAvailableTime)
                    : '';
                const nextAvailableTimeFormatted = nextAvailableTime
                    ? format(R.AvailableFrom, nextAvailableTime)
                    : '';

                return (
                    <NavigationItem
                        key={caregiver.Uid}
                        onClick={() => handleClick(caregiver.Uid)}
                        caption={caregiver.FullName}
                        subCaption={nextAvailableTimeFormatted}
                        additionalText={description}
                        icon={<CaregiverAvatar photoUrl={caregiver.PhotoUrl} />}
                        className={cn(
                            'flex min-w-0 flex-auto cursor-pointer flex-row items-center rounded-2xl p-4 hover:bg-gray-100',
                            {
                                'border-action-orange border-2': isSelected,
                                'border border-gray-400': !isSelected,
                            },
                        )}
                    />
                );
            })}
        </div>
    );

    if (asCard) {
        return <Card alwaysCard={false}>{content}</Card>;
    }

    return content;
};
